import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { CatList } from '@blocks-react/components/List';
import { CatListItem } from '@blocks-react/components/ListItem';
import { CatIconAdd } from '@blocks-react/components/Icons/Add';
import { CatIconWarningCircle } from '@blocks-react/components/Icons/WarningCircle';
import TextHighlight from '@app/components/common/Blocks/TextHighlight';
import SearchFormWithLoader from '@app/components/common/Blocks/SearchFormWithLoader';
import { isEmpty } from '@app/utils';
import {
  debouncedGetUCIDlist,
  resetUCIDStateToIdle
} from '@app/store/account/actions';
import { LIST_ITEMS_SIZES, STATUS } from '@app/constants/commonConstants';
import styles from '../ShareListDrawer.module.scss';

const ShareListUCIDDropdown = ({
  addItemToSelectedList,
  selectedUCIDs,
  setSearchTerm,
  searchTerm
}) => {
  const dispatch = useDispatch();
  const [t] = useTranslation();

  const accountList = useSelector(s => s.account?.customerOrganizations) || [];
  const accountListStatus = useSelector(
    s => s.account?.customerOrganizationsStatus
  );

  const handleUCIDSearchSuggestion = useCallback(
    val => {
      dispatch(resetUCIDStateToIdle());
      if (val.trim().length >= 3) {
        debouncedGetUCIDlist(dispatch, { searchTerm: val });
      }
    },
    [dispatch]
  );

  const onInputChange = val => {
    setSearchTerm(val ?? '');
    handleUCIDSearchSuggestion(val);
  };

  const dropdownList =
    searchTerm.trim().length >= 3
      ? accountList.filter(
          item => !selectedUCIDs.includes(item.customerOrganizationIdentifier)
        )
      : [];

  const getListContent = () => {
    if (accountListStatus === STATUS.REJECTED) {
      return (
        <CatListItem className={`${styles['account-search__error-message']}`}>
          <div className="cat-u-display-flex">
            <CatIconWarningCircle className="cat-u-margin-right-sm" />
            <div>{t('UNABLE_TO_SEARCH_ACCOUNTS')}</div>
          </div>
        </CatListItem>
      );
    }

    if (accountListStatus === STATUS.RESOLVED && isEmpty(dropdownList)) {
      return <CatListItem>{t('NO_MATCHING_ACCOUNTS')}</CatListItem>;
    }

    return dropdownList?.map(item => (
      <CatListItem
        size={LIST_ITEMS_SIZES.SM}
        key={item.customerOrganizationIdentifier}
        value={item.customerOrganizationBusinessName}
        className={`${styles['share-list-dropdown__list_item']}`}
        onClick={() => addItemToSelectedList(item)}
      >
        <div className="cat-u-display-flex justify-content-between align-items-center">
          <div>
            <TextHighlight
              text={item.customerOrganizationBusinessName}
              textToHighlight={searchTerm}
              className={`${styles['share-list__name-truncate']} ${styles['share-list__dropdown-name-width']} cat-u-margin-right-md`}
            />

            <TextHighlight
              text={item.customerOrganizationIdentifier}
              textToHighlight={searchTerm}
            />
          </div>
          <div>
            <CatIconAdd
              className={`${styles['share-list-dropdown__add_account_icon']} d-none`}
            />
          </div>
        </div>
      </CatListItem>
    ));
  };

  return (
    <SearchFormWithLoader
      className={`${styles['share-list__dropdown-height']}`}
      searchTerm={searchTerm}
      isLoading={accountListStatus === STATUS.PENDING}
      searchFormLabel={t('LOOKUP_ACCOUNTS')}
      searchFormPlaceHolder={t('LOOKUP_ACCOUNTS')}
      onChange={onInputChange}
      isResultEmpty={searchTerm.length < 3}
      autoFocus={true}
    >
      <CatList shadow={'subtle'}>{getListContent()}</CatList>
    </SearchFormWithLoader>
  );
};

ShareListUCIDDropdown.propTypes = {
  addItemToSelectedList: PropTypes.func,
  selectedUCIDs: PropTypes.arrayOf(PropTypes.string),
  setSearchTerm: PropTypes.func,
  searchTerm: PropTypes.string
};
export default ShareListUCIDDropdown;
